import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  inject,
  Input
} from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AuthStore } from '../../../store/auth.store';
import { puedeVerEncuestaSupervisionGestion } from '../../../shared/utils/perfil.util';
import { decodeHTMLEntities } from '../../utils/string.util';

import {
  EstadoPlanNegocioOpcion,
  ID_ESTADO_PLAN_NEGOCIO,
  NegocioService,
  ProductorPlanNegocioItem,
  normalizarIdEstadoPlanNegocioFiltro,
  resolverQueryFiltroEstadoPlanNegocio
} from '../../services/negocio.service';

/** Claves de detalle de avance mostradas en el popover del avatar (excluye total y margen_bruto). */
type CampoDetalleAvancePopover =
  | 'diagnostico'
  | 'soluciones'
  | 'plan_de_trabajo'
  | 'plan_de_inversion';
import {
  estadoPlanNegocioRawParaUi,
  getClaseEstadoPlanNegocio as getClaseEstadoPlanNegocioConfig,
  getEtiquetaEstadoPlanNegocio,
  planNegocioCoincideConFiltroEstado,
  planNegocioEstaEnEjecucion,
  planNegocioEstaCerrado
} from '../../config/colores-plan-negocio.config';

/** Acciones del menú por fila */
export type AccionGestion =
  | 'ver_caracterizacion'
  | 'diagnostico'
  | 'soluciones'
  | 'plan_trabajo'
  | 'plan_inversion'
  | 'margen_bruto'
  | 'historial_visitas'
  | 'seguimiento_inversion'
  | 'supervision_grupal'
  | 'supervision_individual'
  | 'encuesta_evaluacion'
  | 'copago';



/**
 * Componente de tarjeta de Gestión (reutilizable).
 * Tabla responsiva con paginador, buscador, filtros, orden por columnas y columna de acciones.
 * Alimentado por GET /api/v1/negocio/perfil/{id_perfil}/jerarquia/{id_jerarquia}.
 */
@Component({
  selector: 'app-tarjeta-gestion',
  templateUrl: './tarjeta-gestion.component.html',
  styleUrls: ['./tarjeta-gestion.component.scss'],
  standalone: false
})
export class TarjetaGestionComponent implements OnInit, OnDestroy {
  private static contadorId = 0;

  /**
   * Circunferencia del anillo SVG (r=16 en viewBox 36×36).
   * Usar esta longitud en dasharray/dashoffset evita depender de `pathLength` + dash en CSS,
   * combinación que en WebKit móvil puede dibujar menos arco del esperado al escalar el SVG.
   */
  readonly longitudAnilloAvatar = 2 * Math.PI * 16;

  @Input() titulo: string = 'Gestión';
  @Input() prefijoSubtitulo: string = 'Gestión y seguimiento SAT a Usuarios INDAP para:';
  @Input() tamanioPaginaInicial: number = 10;
  /** Texto de información mostrado en un popover al hacer clic en el ícono de información. */
  @Input() informacion: string = '';

  /** Identificador único para el popover (evita colisiones con varias tarjetas). */
  public idUnico = `informacion-tarjeta-gestion-${TarjetaGestionComponent.contadorId++}`;

  @Output() accionGestion = new EventEmitter<{
    accion: AccionGestion;
    item: ProductorPlanNegocioItem;
  }>();

  private readonly negocioService = inject(NegocioService);
  private readonly authStore = inject(AuthStore);

  private subscriptions = new Subscription();

  items: ProductorPlanNegocioItem[] = [];
  cargando = false;
  paginaActual = 1;
  tamanioPagina = 10;
  totalPaginas = 1;
  totalRegistros = 0;
  opcionesTamanioPagina = [5, 10, 25, 50];
  terminoBusqueda = '';
  /** null = sin filtro (todos los estados). Alineado con GET .../obtenerEstadosNegocio. */
  idEstadoPlanSeleccionado: number | null = null;
  estadosPlanNegocio: EstadoPlanNegocioOpcion[] = [];
  subtituloDinamico = '';

  // ── Popover único de acciones (evita bug de Ionic con trigger en *ngFor) ──
  itemMenuActivo: ProductorPlanNegocioItem | null = null;
  menuAccionesAbierto = false;
  menuAccionesEvent: Event | undefined;
  /** Alineación vertical respecto al botón ⋯ (side=start). */
  menuAccionesAlignment: 'start' | 'end' = 'start';

  /** Altura aproximada del menú para elegir alineación. */
  private readonly alturaEstimadaMenuAccionesPx = 400;

  // ── Popover único de avance (evita trigger dentro de *ngFor) ──
  itemAvanceActivo: ProductorPlanNegocioItem | null = null;
  popoverAvanceAbierto = false;
  popoverAvanceEvent: Event | undefined;

  readonly filasDetalleAvancePopover: ReadonlyArray<{
    campo: CampoDetalleAvancePopover;
    etiqueta: string;
  }> = [
    { campo: 'diagnostico', etiqueta: 'Diagnóstico' },
    { campo: 'soluciones', etiqueta: 'Soluciones' },
    { campo: 'plan_de_trabajo', etiqueta: 'Plan de trabajo' },
    { campo: 'plan_de_inversion', etiqueta: 'Plan de inversión' }
  ];

  abrirMenuAcciones(event: Event, item: ProductorPlanNegocioItem): void {
    event.stopPropagation();
    this.itemMenuActivo = item;
    this.menuAccionesEvent = event;
    this.menuAccionesAlignment = this.resolverAlineacionMenuAcciones(event);
    this.menuAccionesAbierto = true;
  }

  /**
   * Con side=start el menú queda a la izquierda del botón.
   * start = borde superior del menú alineado al botón (crece hacia abajo).
   * end = borde inferior alineado al botón (crece hacia arriba) si no hay espacio abajo.
   */
  private resolverAlineacionMenuAcciones(event: Event): 'start' | 'end' {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) {
      return 'start';
    }
    const rect = target.getBoundingClientRect();
    const espacioAbajo = window.innerHeight - rect.bottom;
    const espacioArriba = rect.top;

    if (espacioAbajo >= this.alturaEstimadaMenuAccionesPx) {
      return 'start';
    }
    if (espacioArriba >= this.alturaEstimadaMenuAccionesPx) {
      return 'end';
    }
    return espacioAbajo >= espacioArriba ? 'start' : 'end';
  }

  cerrarMenuAcciones(): void {
    this.menuAccionesAbierto = false;
    this.itemMenuActivo = null;
    this.menuAccionesEvent = undefined;
  }

  /** Encuesta de Supervisión: solo Ejecutivo Integral. */
  get puedeVerEncuestaSupervision(): boolean {
    return puedeVerEncuestaSupervisionGestion(this.authStore.obtenerPerfilActivo());
  }

  abrirPopoverAvance(event: Event, item: ProductorPlanNegocioItem): void {
    event.stopPropagation();
    if (this.getAvanceTotal(item) == null) return;
    this.itemAvanceActivo = item;
    this.popoverAvanceEvent = event;
    this.popoverAvanceAbierto = true;
  }

  cerrarPopoverAvance(): void {
    this.popoverAvanceAbierto = false;
    this.itemAvanceActivo = null;
    this.popoverAvanceEvent = undefined;
  }

  sortBy = 'nombre';
  sortOrder: 'asc' | 'desc' = 'asc';
  readonly columnasOrdenables: { id: string; label: string; sortKey: string }[] = [
    { id: 'productor', label: 'Nombre', sortKey: 'nombre' },
    { id: 'unidad', label: 'Unidad Operativa', sortKey: 'unidad_operativa' },
    { id: 'area', label: 'Agencia de Área', sortKey: 'area'},
    { id: 'comuna', label: 'Comuna', sortKey: 'comuna' },
    { id: 'ultima_visita', label: 'Últ. visita', sortKey: 'ultima_visita' },
    { id: 'ultimo_mb', label: 'Últ. MB', sortKey: 'ultimo_mb' },
    { id: 'ultimo_ev', label: 'Últ. EV', sortKey: 'ultimo_ev' },
    { id: 'diag_activo', label: '¿Diag. Activo?', sortKey: 'diag_activo' },
    { id: 'estado_plan', label: 'Estado plan', sortKey: 'estado' }
  ];

  ngOnInit(): void {
    this.tamanioPagina = this.tamanioPaginaInicial;
    this.cargarEstadosPlanNegocio();

    // Una sola carga al alinear perfil + UO + área (antes: emisión inicial de perfil + cargarDatos() final = 2× el mismo listado).
    const authCtxSub = combineLatest([
      this.authStore.perfilActivo$,
      this.authStore.idRegionSeleccionada$,
      this.authStore.unidadOperativaActiva$,
      this.authStore.idAreaSeleccionada$
    ])
      .pipe(
        map(([perfil, region, unidad, area]) => ({
          perfilId: perfil?.id_perfil ?? null,
          region,
          unidad,
          area
        })),
        distinctUntilChanged(
          (a, b) =>
            a.perfilId === b.perfilId &&  a.region === b.region && a.unidad === b.unidad && a.area === b.area
        )
      )
      .subscribe(() => {
        this.actualizarSubtitulo();
        this.paginaActual = 1;
        this.cargarDatos();
      });
    this.subscriptions.add(authCtxSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Vuelve a pedir el listado al servidor (GET negocio jerárquico).
   * Útil al reentrar en `/gestion` desde otra ruta: Ionic puede reutilizar el componente sin disparar `ngOnInit`.
   */
  refrescarListadoDesdeEntrada(): void {
    this.cargarDatos();
  }

  private cargarEstadosPlanNegocio(): void {
    this.negocioService.obtenerEstadosNegocio().subscribe({
      next: (lista) => {
        const arr = Array.isArray(lista) ? lista : [];
        this.estadosPlanNegocio = [...arr].sort(
          (a, b) => a.id_estado_plan_negocio - b.id_estado_plan_negocio
        );
      },
      error: () => {
        this.estadosPlanNegocio = [];
      }
    });
  }

  onFiltroEstadoPlanChange(event: Event): void {
    const v = (event as CustomEvent).detail?.value;
    this.idEstadoPlanSeleccionado = normalizarIdEstadoPlanNegocioFiltro(v);
    this.paginaActual = 1;
    this.cargarDatos();
  }

  /** Texto en el selector: etiqueta canónica (Ejecución aunque el catálogo API diga Implementación). */
  etiquetaOpcionEstadoPlan(e: EstadoPlanNegocioOpcion): string {
    return getEtiquetaEstadoPlanNegocio(e.id_estado_plan_negocio);
  }

  /**
   * Clase en `ion-select-option` → Ionic la copia al `ion-item` del popover (no transfiere data-*).
   * Los círculos se pintan en global.scss vía `ion-radio::part(label)` (el texto no va en ion-label).
   */
  claseOptPopoverEstadoPlan(idEstado: number): string {
    if (idEstado === ID_ESTADO_PLAN_NEGOCIO.DIAGNOSTICO) return 'select-opt-pn-diagnostico';
    if (idEstado === ID_ESTADO_PLAN_NEGOCIO.DISENO) return 'select-opt-pn-diseno';
    if (idEstado === ID_ESTADO_PLAN_NEGOCIO.EJECUCION) return 'select-opt-pn-ejecucion';
    return 'select-opt-pn-default';
  }

  private actualizarSubtitulo(): void {
    const idJerarquia = this.authStore.obtenerIdJerarquiaParaEndpoint();
    this.subtituloDinamico =
      idJerarquia === -1
        ? `${this.prefijoSubtitulo} Todas las Unidades Operativas`
        : `${this.prefijoSubtitulo} Unidad seleccionada`;
  }

  cargarDatos(): void {
    const perfil = this.authStore.obtenerPerfilActivo();
    const idJerarquia = this.authStore.obtenerIdJerarquiaParaEndpoint();
    if (perfil?.id_perfil == null) {
      this.items = [];
      this.totalRegistros = 0;
      this.totalPaginas = 1;
      return;
    }

    this.cargando = true;
    const idFiltroEstado = normalizarIdEstadoPlanNegocioFiltro(
      this.idEstadoPlanSeleccionado
    );
    this.negocioService
      .obtenerProductoresPlanNegocio(perfil.id_perfil, idJerarquia, {
        page: this.paginaActual,
        size: this.tamanioPagina,
        sort: this.sortBy,
        order: this.sortOrder,
        search: this.terminoBusqueda.trim() || undefined,
        ...resolverQueryFiltroEstadoPlanNegocio(idFiltroEstado)
      })
      .subscribe({
        next: (resp) => {
          const rawItems = resp.items ?? [];
          this.items =
            idFiltroEstado == null
              ? rawItems
              : rawItems.filter((item) =>
                  planNegocioCoincideConFiltroEstado(
                    item.plan_negocio,
                    idFiltroEstado
                  )
                );
          this.totalRegistros = resp.total ?? 0;
          this.totalPaginas = resp.pages ?? 1;
          this.paginaActual = resp.page ?? this.paginaActual;
          this.tamanioPagina = resp.size ?? this.tamanioPagina;
          this.cargando = false;
        },
        error: () => {
          this.items = [];
          this.totalRegistros = 0;
          this.totalPaginas = 1;
          this.cargando = false;
        }
      });
  }

  buscar(): void {
    this.paginaActual = 1;
    this.cargarDatos();
  }

  limpiarBusqueda(): void {
    this.terminoBusqueda = '';
    this.paginaActual = 1;
    this.cargarDatos();
  }

  onSortColumn(columnaId: string): void {
    const col = this.columnasOrdenables.find(c => c.id === columnaId);
    if (!col) return;
    if (this.sortBy === col.sortKey) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = col.sortKey;
      this.sortOrder = 'asc';
    }
    this.paginaActual = 1;
    this.cargarDatos();
  }

  isColumnaOrdenActiva(sortKey: string): boolean {
    return this.sortBy === sortKey;
  }

  paginaAnterior(): void {
    if (this.paginaActual > 1) {
      this.paginaActual--;
      this.cargarDatos();
    }
  }

  paginaSiguiente(): void {
    if (this.paginaActual < this.totalPaginas) {
      this.paginaActual++;
      this.cargarDatos();
    }
  }

  irAPagina(pagina: number): void {
    if (pagina >= 1 && pagina <= this.totalPaginas) {
      this.paginaActual = pagina;
      this.cargarDatos();
    }
  }

  onTamanioPaginaChange(event: Event): void {
    const value = (event as CustomEvent).detail?.value;
    if (value != null) {
      this.tamanioPagina = Number(value);
      this.paginaActual = 1;
      this.cargarDatos();
    }
  }

  getPaginasArray(): number[] {
    const max = 5;
    let inicio = Math.max(1, this.paginaActual - Math.floor(max / 2));
    let fin = Math.min(this.totalPaginas, inicio + max - 1);
    if (fin - inicio < max - 1) {
      inicio = Math.max(1, fin - max + 1);
    }
    const paginas: number[] = [];
    for (let i = inicio; i <= fin; i++) paginas.push(i);
    return paginas;
  }

  /** Iniciales para avatar: 1ª letra nombre + 1ª letra primer_apellido */
  getIniciales(item: ProductorPlanNegocioItem): string {
    const n = decodeHTMLEntities(item.nombre ?? '').trim();
    const a = decodeHTMLEntities(item.primer_apellido ?? '').trim();
    if (n && a) return (n[0] + a[0]).toUpperCase();
    if (n.length >= 2) return n.slice(0, 2).toUpperCase();
    return n ? n[0].toUpperCase() : '?';
  }

  getNombreCompleto(item: ProductorPlanNegocioItem): string {
    const partes = [
      decodeHTMLEntities(item.nombre),
      decodeHTMLEntities(item.primer_apellido)
    ].filter(Boolean);
    return partes.length ? partes.join(' ') : '-';
  }


  /**
   * Avance general 0..100 desde `detalle_avance.total`.
   * Si no existe o no es número válido → null (no se muestra anillo / no abre popover).
   */
  getAvanceTotal(item: ProductorPlanNegocioItem | null | undefined): number | null {
    const raw = item?.detalle_avance?.total;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.min(100, Math.round(n)));
  }

  getTextoAvance(item: ProductorPlanNegocioItem | null | undefined): string {
    const t = this.getAvanceTotal(item);
    return t == null ? 'Sin avance disponible' : `Avance general: ${t}%`;
  }

  /**
   * Porcentaje 0..100 para un campo de `detalle_avance` (distinto de `total`).
   */
  getPorcentajeDetalleAvance(
    item: ProductorPlanNegocioItem | null | undefined,
    campo: CampoDetalleAvancePopover
  ): number | null {
    const raw = item?.detalle_avance?.[campo];
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.min(100, Math.round(n)));
  }

  textoPorcentajeDetalle(
    item: ProductorPlanNegocioItem | null | undefined,
    campo: CampoDetalleAvancePopover
  ): string {
    const p = this.getPorcentajeDetalleAvance(item, campo);
    return p == null ? '—' : `${p}%`;
  }

  private parseFechaApi(valor: string): Date | null {
    const d = new Date(valor.trim().replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d;
  }

  formatearFecha(fechaStr: string | null | undefined): string {
    if (!fechaStr?.trim()) return '-';
    const d = this.parseFechaApi(fechaStr);
    if (!d) return fechaStr;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }

  getEstadoPlan(item: ProductorPlanNegocioItem): string {
    return getEtiquetaEstadoPlanNegocio(estadoPlanNegocioRawParaUi(item.plan_negocio));
  }

  /**
   * Clase CSS del badge según plan (prioriza id_estado_plan_negocio; null → Diagnóstico).
   */
  getClaseEstadoPlanNegocioDeItem(item: ProductorPlanNegocioItem): string {
    return getClaseEstadoPlanNegocioConfig(estadoPlanNegocioRawParaUi(item.plan_negocio));
  }

  esPlanEnEjecucion(item: ProductorPlanNegocioItem | null | undefined): boolean {
    return planNegocioEstaEnEjecucion(item?.plan_negocio ?? null);
  }

  esPlanCerrado(item: ProductorPlanNegocioItem | null | undefined): boolean {
    return planNegocioEstaCerrado(item?.plan_negocio ?? null);
  }

  onAccion(accion: AccionGestion, item: ProductorPlanNegocioItem): void {
    this.accionGestion.emit({ accion, item });
  }

  /** Misma navegación que la acción del menú «Caracterización». */
  irACaracterizacion(event: Event, item: ProductorPlanNegocioItem): void {
    event.preventDefault();
    event.stopPropagation();
    if (!item?.id_productor) return;
    this.onAccion('ver_caracterizacion', item);
  }
}
